import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import Navbar from "@/components/storefront/Navbar";
import Footer from "@/components/storefront/Footer";
import { Button } from "@/components/ui/button";
import { CheckCircle2, XCircle, Loader2, Download, CalendarCheck } from "lucide-react";
import { useApp } from "@/contexts/AppContext";
import { api, API } from "@/lib/api";

export default function CheckoutSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const paypalOrderId = params.get("paypal_order");
  const paypalToken = params.get("token");
  const { t, clearCart, lang } = useApp();
  const [status, setStatus] = useState("polling");
  const [orderInfo, setOrderInfo] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!sessionId && !paypalOrderId) {
      setStatus("failed");
      return;
    }

    // PayPal flow: capture once
    if (paypalOrderId && paypalToken) {
      (async () => {
        try {
          const { data } = await api.post(`/paypal/capture/${paypalToken}`);
          if (data?.ok) {
            clearCart();
            setOrderInfo({ order_id: paypalOrderId, order_number: paypalOrderId.slice(0, 8), kind: "product" });
            setStatus("paid");
          } else {
            setStatus("failed");
          }
        } catch (e) {
          console.warn("[paypal-capture] failed", e);
          setStatus("failed");
        }
      })();
      return;
    }

    // Stripe flow: poll status
    let attempts = 0;
    let cancelled = false;
    const poll = async () => {
      attempts += 1;
      if (attempts > 10 || cancelled) {
        if (!cancelled) setStatus("failed");
        return;
      }
      try {
        const { data } = await api.get(`/checkout/status/${sessionId}`);
        if (data.payment_status === "paid") {
          clearCart();
          setOrderInfo(data);
          setStatus("paid");
          return;
        }
        if (data.status === "expired") {
          setStatus("failed");
          return;
        }
      } catch (e) {
        console.warn("[checkout-status] poll error", e);
      }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId, paypalOrderId, paypalToken, clearCart]);

  const downloadFile = async () => {
    if (!orderInfo?.order_id) return;
    try {
      const res = await fetch(`${API}/orders/${orderInfo.order_id}/download`);
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        alert(err.detail || "File not ready yet");
        return;
      }
      const blob = await res.blob();
      const cd = res.headers.get("Content-Disposition") || "";
      const m = /filename="([^"]+)"/.exec(cd);
      const fname = m ? m[1] : "download";
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = fname;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.warn("[download] failed", e);
    }
  };

  const isBooking = orderInfo?.kind === "booking";

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Navbar />
      <main className="flex-1 flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-surface border border-border rounded-2xl p-10 text-center shadow-ambient" data-testid="checkout-status-card">
          {status === "polling" && (
            <>
              <Loader2 className="h-12 w-12 mx-auto text-primary animate-spin mb-4" />
              <h1 className="text-2xl font-bold mb-2">{t.paymentProcessing}</h1>
            </>
          )}
          {status === "paid" && (
            <>
              {isBooking ? <CalendarCheck className="h-12 w-12 mx-auto text-success mb-4" /> : <CheckCircle2 className="h-12 w-12 mx-auto text-success mb-4" />}
              <h1 data-testid="payment-success-title" className="text-2xl font-bold mb-2">{t.paymentSuccess}</h1>
              <p className="text-muted-foreground text-sm mb-6">
                {isBooking ? (lang === "ar" ? "تم تأكيد حجزك" : "Your booking is confirmed") : `Order #${orderInfo?.order_number}`}
              </p>
              <div className="flex flex-col gap-2">
                {!isBooking && (
                  <Button onClick={downloadFile} data-testid="download-file-button" className="gap-2">
                    <Download className="h-4 w-4" />
                    {t.downloadFile}
                  </Button>
                )}
                {!isBooking && <p className="text-[11px] text-muted-foreground">{t.pendingFile}</p>}
                <Link to="/my-orders" data-testid="my-orders-link">
                  <Button variant="outline" className="w-full">{lang === "ar" ? "تتبع طلبي" : "Track my order"}</Button>
                </Link>
                <Link to="/" data-testid="back-to-storefront-link">
                  <Button variant="ghost" className="w-full">{t.backToStore}</Button>
                </Link>
              </div>
            </>
          )}
          {status === "failed" && (
            <>
              <XCircle className="h-12 w-12 mx-auto text-destructive mb-4" />
              <h1 className="text-2xl font-bold mb-2">{t.paymentFailed}</h1>
              <Link to="/cart"><Button variant="outline" className="mt-4">{t.cart}</Button></Link>
            </>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
